import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { qk } from '../../lib/query'

/**
 * Recording what the human did with a suggestion.
 *
 * Shared by both faces of the reply card. The front reports sent_verbatim / edited / rejected;
 * the back reports `rewritten` — a reply typed from scratch while a suggestion was on screen.
 * That last one used to be recorded nowhere: the composer was a separate component that had
 * never heard of the suggestion, so the strongest signal available — an agent choosing not even
 * to start from our answer — vanished silently.
 */
export function useSuggestionFeedback(conversationId: string) {
  const qc = useQueryClient()
  // The same query the card runs; react-query serves one request to both.
  const { data: stored } = useQuery({
    queryKey: qk.storedSuggestion(conversationId),
    queryFn: () => api.storedSuggestion(conversationId),
  })

  /** Only what a feedback row needs — so a freshly generated card can report against itself. */
  type Reportable = {
    suggestion: string
    entryIds: string[]
    model: string | null
    certainty: number | null
    certaintyBand: 'low' | 'medium' | 'high' | null
  }

  const send = (
    outcome: 'sent_verbatim' | 'edited' | 'rewritten' | 'rejected',
    finalText: string | null,
    from?: Reportable | null,
  ) => {
    const s: Reportable | null =
      from ?? (stored && stored.kind !== 'error' ? stored : null)
    // No suggestion was on the table, so there is nothing to report. A plain reply is not
    // feedback about an answer that was never offered.
    if (!s?.suggestion) return
    void api
      .suggestFeedback(conversationId, {
        suggestionText: s.suggestion,
        outcome,
        finalText,
        knowledgeEntryIds: s.entryIds,
        model: s.model,
        certainty: s.certainty,
        certaintyBand: s.certaintyBand,
      })
      // The server drops the stored row once acted on; drop the cached copy too, or the card
      // reappears offering a reply that was already sent.
      .finally(() => qc.invalidateQueries({ queryKey: qk.storedSuggestion(conversationId) }))
  }

  return { stored, send, hasSuggestion: Boolean(stored && stored.kind !== 'error' && stored.suggestion) }
}
